import { Layout } from '@/bloks/Global/Layout';
import { ClientProviders } from '@/client/ClientProviders';
import { GlobalStoryblok } from '@/components';
import { AccountantsProvider } from '@/contexts/accountants/AccountantsProvider';
import { fetchAllAccountants, fetchCities, searchAccountants } from '@/server/accountants';
import { ServerPageConfig, getServerPageConfig } from '@/server/config';
import { getBreadcrumbs } from '@/server/config/getBreadcrumbs';
import { getGlobalStory, getSingleStoryBySlug } from '@/server/storyblok';
import { detectEnabledModules } from '@/server/storyblok/enabledModules';
import { processModuleHandlers } from '@/server/storyblok/enabledModules/processModuleHandlers';
import { PageDataProviderParams } from '@/server/storyblok/enabledModules/types';
import { ISbStoryData, SbParamsExtended } from '@/types/storyblok';
import { capitalizeEachWord } from '@/utils';
import { resolveRelations, version } from '@/utils/storyblok';
import { GetServerSideProps } from 'next';

// Component based on Storyblok blog post example
// https://github.com/storyblok/nextjs-multilanguage-website/blob/main/pages/%5B%5B...slug%5D%5D.js

interface Props {
	story: ISbStoryData;
	globalStory: ISbStoryData<GlobalStoryblok>;
	locale: string;
	preview: boolean;
	accountants: any[];
	cities: any[];
	searchAddress: string;
	config: ServerPageConfig;
	providerParams: PageDataProviderParams;
}

const Page: React.FC<Props> = ({ accountants, cities, searchAddress, ...props }) => {
	return (
		<ClientProviders providerParams={props.providerParams}>
			<AccountantsProvider accountants={accountants} cities={cities} searchAddress={searchAddress}>
				<Layout {...props} />
			</AccountantsProvider>
		</ClientProviders>
	);
};

export default Page;

export const getServerSideProps: GetServerSideProps<Props> = async ({
	locale = 'sv',
	locales,
	query,
	preview = false,
}) => {
	const sbParams: SbParamsExtended = {
		version,
		language: locale as SbParamsExtended['language'],
		resolve_relations: resolveRelations,
	};
	const slug = 'hitta-redovisningsbyra';

	if (preview) {
		sbParams.version = 'draft';
		sbParams.cv = Date.now();
	}

	const searchAddress = (Array.isArray(query?.address) ? query.address[0] : query.address) || '';

	const [story, globalStory, accountants, cities] = await Promise.all([
		getSingleStoryBySlug(sbParams, slug),
		getGlobalStory(sbParams),
		searchAddress ? searchAccountants(searchAddress, undefined, undefined) : fetchAllAccountants(),
		fetchCities(),
	]);

	const breadcrumbs = await getBreadcrumbs(story.full_slug, story.name, sbParams);
	let dynamicMetaTitle;
	let dynamicMetaDescription;
	let canonicalPath: `/${string}` = `/hitta-redovisningsbyra`;
	const searchParams = new URLSearchParams();

	if (accountants?.length > 0 && searchAddress) {
		searchParams.set('address', searchAddress);
		canonicalPath = `/hitta-redovisningsbyra?${searchParams}`;
		const capitalizedCity = capitalizeEachWord(searchAddress);
		story.content.meta = [{ canonical: { url: canonicalPath } }];
		breadcrumbs.push({
			item: canonicalPath,
			name: `Redovisningsbyråer i ${capitalizedCity}`,
		});
		dynamicMetaTitle = `Redovisningsbyråer i ${capitalizedCity} | Fortnox`;
		dynamicMetaDescription = `Hitta en redovisningskonsult i ${capitalizedCity} som kan skräddarsy ert samarbete efter dina önskemål.`;
	}

	const enabledModules = detectEnabledModules(story);
	const modulesData = await processModuleHandlers(story, enabledModules, sbParams);

	return {
		props: {
			config: getServerPageConfig({ slug, story, breadcrumbs }),
			story: story || false,
			globalStory: globalStory || false,
			preview,
			locale,
			locales,
			accountants,
			cities,
			searchAddress,
			dynamicMeta: {
				title: dynamicMetaTitle ?? '',
				description: dynamicMetaDescription ?? '',
				url: canonicalPath,
				canonical: canonicalPath,
			},
			providerParams: modulesData,
		},
	};
};
